<!-- 商家端 - 售后退款列表 -->
<template>
  <div>
    <!-- 搜索栏 -->
    <ContentWrap :body-style="{ padding: '16px 16px 2px', overflow: 'visible' }">
      <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="88px">
        <el-form-item label="退款编号" prop="no">
          <el-input
            v-model="queryParams.no"
            class="!w-200px"
            clearable
            placeholder="请输入退款编号"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="订单编号" prop="orderNo">
          <el-input
            v-model="queryParams.orderNo"
            class="!w-200px"
            clearable
            placeholder="请输入订单编号"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="商品名称" prop="spuName">
          <el-input
            v-model="queryParams.spuName"
            class="!w-200px"
            clearable
            placeholder="请输入商品名称"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="售后方式" prop="way">
          <el-select v-model="queryParams.way" class="!w-160px" clearable placeholder="全部">
            <el-option
              v-for="item in wayOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="售后状态" prop="status">
          <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="全部">
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <div class="flex flex-wrap items-center gap-8px">
            <el-button @click="handleQuery">
              <Icon class="mr-5px" icon="ep:search" />搜索
            </el-button>
            <el-button @click="resetQuery">
              <Icon class="mr-5px" icon="ep:refresh" />重置
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </ContentWrap>

    <!-- 列表 -->
    <ContentWrap :body-style="{ padding: '16px', overflow: 'visible' }">
      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column label="退款编号" min-width="180" prop="no" show-overflow-tooltip />
        <el-table-column label="订单编号" min-width="160" prop="orderNo" show-overflow-tooltip />
        <el-table-column label="商品名称" min-width="180" prop="spuName" show-overflow-tooltip />
        <el-table-column label="售后方式" width="110">
          <template #default="{ row }">
            <el-tag size="small" :type="asWay(row) === 20 ? 'success' : 'primary'">
              {{ wayLabel(row.way) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="退款金额" width="110">
          <template #default="{ row }">{{ fenToYuan(row.refundPrice) }} 元</template>
        </el-table-column>
        <el-table-column label="售后状态" width="120">
          <template #default="{ row }">
            <el-tag size="small" :type="statusTagType(row.status)">{{
              statusLabel(row.status)
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="申请时间"
          width="170"
          :formatter="dateFormatter"
          prop="createTime"
        />
        <el-table-column label="操作" width="240" fixed="right">
          <template #default="{ row }">
            <div class="flex flex-wrap items-center gap-x-4px">
              <el-button link type="primary" @click="openDetail(row.id)">查看</el-button>
              <el-button v-if="asStatus(row) === 10" link type="success" @click="openAgree(row)">
                同意
              </el-button>
              <el-button v-if="asStatus(row) === 10" link type="danger" @click="openDisagree(row)">
                拒绝
              </el-button>
              <el-button
                v-if="asStatus(row) === 30"
                link
                type="success"
                @click="handleReceive(row.id)"
              >
                确认收货
              </el-button>
              <el-button
                v-if="asStatus(row) === 40 && asWay(row) !== 30 && !row.payRefundId"
                link
                type="warning"
                @click="handleRefund(row.id)"
              >
                确认退款
              </el-button>
              <el-button
                v-if="asStatus(row) === 41 && asWay(row) === 30"
                link
                type="warning"
                @click="openExchangeDelivery(row)"
              >
                填写换货物流
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </ContentWrap>

    <!-- 售后详情：留在 /store 壳内，走 merchant-api -->
    <el-drawer v-model="detailVisible" :title="detailTitle" size="560px" destroy-on-close>
      <div v-loading="detailLoading">
        <template v-if="detail">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="退款编号">{{ detail.no }}</el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag size="small" :type="statusTagType(detail.status)">
                {{ statusLabel(detail.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="订单编号">{{ detail.orderNo || '—' }}</el-descriptions-item>
            <el-descriptions-item label="售后方式">
              <el-tag
                size="small"
                :type="asWay(detail) === 30 ? 'warning' : asWay(detail) === 20 ? 'success' : 'primary'"
              >
                {{ wayLabel(detail.way) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="商品" :span="2">{{ detail.spuName || '—' }}</el-descriptions-item>
            <el-descriptions-item label="数量">{{ detail.count ?? '—' }}</el-descriptions-item>
            <el-descriptions-item label="退款金额">
              <template v-if="asWay(detail) === 30">换货无退款</template>
              <template v-else>{{ fenToYuan(detail.refundPrice) }} 元</template>
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.usePoint" label="使用积分">
              {{ detail.usePoint }} 分
              <span class="ml-6px text-12px text-gray-400">退款成功后原路退回</span>
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.pointPrice" label="积分抵扣">
              {{ fenToYuan(detail.pointPrice) }} 元
            </el-descriptions-item>
            <el-descriptions-item v-if="detail.givePoint" label="赠送积分">
              {{ detail.givePoint }} 分
              <span class="ml-6px text-12px text-gray-400">退款成功后扣回</span>
            </el-descriptions-item>
            <el-descriptions-item label="申请原因" :span="2">
              {{ detail.applyReason || '—' }}
            </el-descriptions-item>
            <el-descriptions-item label="补充描述" :span="2">
              {{ detail.applyDescription || '—' }}
            </el-descriptions-item>
            <el-descriptions-item label="凭证图片" :span="2">
              <template v-if="detail.applyPicUrls?.length">
                <el-image
                  v-for="(url, index) in detail.applyPicUrls"
                  :key="index"
                  :src="url"
                  :preview-src-list="detail.applyPicUrls"
                  :initial-index="index"
                  class="mr-10px h-60px w-60px"
                  fit="cover"
                  preview-teleported
                />
              </template>
              <span v-else>—</span>
            </el-descriptions-item>
            <el-descriptions-item
              v-if="(asWay(detail) === 20 || asWay(detail) === 30) && detail.returnName"
              label="退货地址"
              :span="2"
            >
              {{ detail.returnName }} {{ detail.returnMobile }} /
              {{ detail.returnAreaName || '' }} {{ detail.returnDetailAddress }}
            </el-descriptions-item>
            <el-descriptions-item
              v-if="asWay(detail) === 30 && detail.exchangeLogisticsNo"
              label="换货物流"
              :span="2"
            >
              {{ detail.exchangeLogisticsNo }}
              <span v-if="detail.exchangeDeliveryTime" class="ml-8px text-12px text-gray-400">
                {{ formatDate(detail.exchangeDeliveryTime) }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="申请时间" :span="2">
              {{ formatDate(detail.createTime) || '—' }}
            </el-descriptions-item>
          </el-descriptions>

          <div class="mt-20px flex flex-wrap gap-10px">
            <el-button v-if="asStatus(detail) === 10" type="primary" @click="openAgree(detail)">
              同意售后
            </el-button>
            <el-button v-if="asStatus(detail) === 10" type="danger" @click="openDisagree(detail)">
              拒绝售后
            </el-button>
            <el-button
              v-if="asStatus(detail) === 30"
              type="primary"
              @click="handleReceive(detail.id)"
            >
              确认收货
            </el-button>
            <el-button v-if="asStatus(detail) === 30" @click="handleRefuse(detail.id)">
              拒绝收货
            </el-button>
            <el-button
              v-if="asStatus(detail) === 41 && asWay(detail) === 30"
              type="warning"
              @click="openExchangeDelivery(detail)"
            >
              填写换货物流
            </el-button>
            <el-button
              v-if="
                asWay(detail) !== 30 && (asStatus(detail) === 40 || asStatus(detail) === 50)
              "
              type="primary"
              :disabled="refundButtonDisabled"
              :loading="refundSubmitting"
              @click="handleRefund(detail.id)"
            >
              {{ refundButtonText }}
            </el-button>
          </div>

          <el-alert
            class="mt-16px"
            type="info"
            :closable="false"
            title="处理提醒"
            description="申请中：可同意或拒绝。退货退款需等买家寄回后确认收货，再到「等待退款」点击确认退款。同意时可下调退款金额。"
          />

          <el-divider v-if="detail.logs?.length" content-position="left">售后日志</el-divider>
          <el-timeline v-if="detail.logs?.length">
            <el-timeline-item
              v-for="log in detail.logs"
              :key="log.id"
              :timestamp="formatDate(log.createTime)"
              placement="top"
            >
              {{ log.content }}
            </el-timeline-item>
          </el-timeline>
        </template>
      </div>
    </el-drawer>

    <!-- 同意售后 -->
    <Dialog v-model="agreeVisible" title="同意售后" width="480px">
      <el-form
        ref="agreeFormRef"
        v-loading="agreeLoading"
        :model="agreeForm"
        :rules="agreeRules"
        label-width="120px"
      >
        <template v-if="agreeWay === 30">
          <el-alert
            type="info"
            :closable="false"
            title="换货不涉及退款"
            description="同意后买家寄回原件，您确认收货后发出同规格换货。"
            class="mb-12px"
          />
        </template>
        <template v-else>
          <el-form-item label="申请退款金额">
            <span>{{ fenToYuan(agreeApplyPrice) }} 元</span>
          </el-form-item>
          <el-form-item v-if="agreeItemPayPrice != null" label="商品实付">
            <span>{{ fenToYuan(agreeItemPayPrice) }} 元</span>
          </el-form-item>
          <el-form-item v-if="agreeUsePoint" label="退还积分">
            <span>{{ agreeUsePoint }} 分</span>
            <span class="ml-8px text-12px text-gray-400">确认退款后自动退回买家</span>
          </el-form-item>
          <el-form-item v-if="agreePointPrice" label="积分抵扣">
            <span>{{ fenToYuan(agreePointPrice) }} 元</span>
          </el-form-item>
          <el-form-item label="同意退款金额" prop="refundPriceYuan">
            <el-input-number
              v-model="agreeForm.refundPriceYuan"
              :min="0.01"
              :max="agreeMaxYuan"
              :precision="2"
              :step="0.01"
              class="!w-220px"
            />
            <span class="ml-8px text-gray-400">元</span>
          </el-form-item>
          <div class="mb-12px pl-120px text-13px text-gray-500">
            可下调为部分退款，不可高于申请金额
          </div>
        </template>
        <el-form-item v-if="agreeWay === 20 || agreeWay === 30" label="退货地址">
          <div v-if="agreeReturnReady" class="text-13px leading-22px">
            <div>{{ agreeReturn.returnName }} {{ agreeReturn.returnMobile }}</div>
            <div class="text-gray-500">{{ agreeReturnAddressLine }}</div>
            <div class="mt-4px text-12px text-gray-400">同意后将下发给买家</div>
          </div>
          <el-alert
            v-else
            type="warning"
            :closable="false"
            title="尚未配置店铺退货地址"
            description="请先在「店铺设置」完善退货地址，再同意换货/退货退款。"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button
          :disabled="
            agreeLoading || ((agreeWay === 20 || agreeWay === 30) && !agreeReturnReady)
          "
          type="primary"
          @click="submitAgree"
        >
          确 定
        </el-button>
        <el-button @click="agreeVisible = false">取 消</el-button>
      </template>
    </Dialog>

    <!-- 发出换货 -->
    <Dialog v-model="exchangeVisible" title="填写换货物流" width="480px">
      <el-form
        ref="exchangeFormRef"
        v-loading="exchangeLoading"
        :model="exchangeForm"
        :rules="exchangeRules"
        label-width="100px"
      >
        <el-form-item label="物流公司" prop="logisticsId">
          <el-select
            v-model="exchangeForm.logisticsId"
            filterable
            class="!w-full"
            placeholder="请选择物流公司"
          >
            <el-option
              v-for="item in exchangeExpressList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="物流单号" prop="logisticsNo">
          <el-input v-model="exchangeForm.logisticsNo" placeholder="请输入换货快递单号" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button :disabled="exchangeLoading" type="primary" @click="submitExchangeDelivery">
          确 定
        </el-button>
        <el-button @click="exchangeVisible = false">取 消</el-button>
      </template>
    </Dialog>

    <!-- 拒绝售后 -->
    <Dialog v-model="disagreeVisible" title="拒绝售后" width="480px">
      <el-form v-loading="disagreeLoading" :model="disagreeForm" label-width="80px">
        <el-form-item label="审批备注">
          <el-input
            v-model="disagreeForm.auditReason"
            :rows="3"
            placeholder="请输入拒绝原因"
            type="textarea"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button :disabled="disagreeLoading" type="primary" @click="submitDisagree">
          确 定
        </el-button>
        <el-button @click="disagreeVisible = false">取 消</el-button>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import * as MerchantAfterSaleApi from '@/api/merchant/aftersale'
import * as MerchantShopApi from '@/api/merchant/shop'
import * as MerchantExpressApi from '@/api/merchant/delivery/express'
import type { DeliveryExpressSimpleVO } from '@/api/merchant/delivery/express'
import { getAreaTree } from '@/api/merchant/system/area'
import { formatReturnAddressLine } from '@/utils/areaFormat'
import { dateFormatter, formatDate } from '@/utils/formatTime'
import { fenToYuan, fenToYuanNumber, yuanToFen } from '@/utils'

defineOptions({ name: 'MerchantAfterSaleList' })

const message = useMessage()

/**
 * 售后状态 / 方式：与 AfterSaleStatusEnum、AfterSaleWayEnum 对齐。
 * 不要用 0~5「待处理」那套旧值——后端 @InEnum 会直接校验失败（表现为服务器错误）。
 */
const statusOptions = [
  { value: 10, label: '申请中' },
  { value: 20, label: '卖家通过' },
  { value: 30, label: '待卖家收货' },
  { value: 40, label: '等待退款' },
  { value: 41, label: '待商家发换货' },
  { value: 42, label: '商家已发换货' },
  { value: 50, label: '已完成' },
  { value: 51, label: '换货完成' },
  { value: 61, label: '买家取消' },
  { value: 62, label: '卖家拒绝' },
  { value: 63, label: '卖家拒绝收货' }
]
const wayOptions = [
  { value: 10, label: '仅退款' },
  { value: 20, label: '退货退款' },
  { value: 30, label: '换货' }
]
const statusMap: Record<number, string> = Object.fromEntries(
  statusOptions.map((i) => [i.value, i.label])
)
const statusTagMap: Record<number, any> = {
  10: 'warning',
  20: 'primary',
  30: 'warning',
  40: 'warning',
  41: 'warning',
  42: 'primary',
  50: 'success',
  51: 'success',
  61: 'info',
  62: 'danger',
  63: 'danger'
}

const asStatus = (row?: { status?: number | string | null }) => {
  const n = Number(row?.status)
  return Number.isFinite(n) ? n : -1
}
const asWay = (row?: { way?: number | string | null }) => {
  const n = Number(row?.way)
  return Number.isFinite(n) ? n : -1
}
const statusLabel = (s?: number | string | null) => {
  const n = Number(s)
  return Number.isFinite(n) && statusMap[n] ? statusMap[n] : s == null || s === '' ? '—' : `未知(${s})`
}
const statusTagType = (s?: number | string | null) => statusTagMap[Number(s)] || 'info'
const wayLabel = (way?: number | string | null) => {
  const n = Number(way)
  if (n === 30) return '换货'
  if (n === 20) return '退货退款'
  if (n === 10) return '仅退款'
  return way == null || way === '' ? '—' : `未知(${way})`
}

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  no: '',
  orderNo: '',
  spuName: '',
  status: undefined as number | undefined,
  way: undefined as number | undefined
})

/** 只传后端认可的查询字段，避免空串 / 非法 status=0 触发 @InEnum */
const buildQuery = () => {
  const params: Record<string, any> = {
    pageNo: queryParams.pageNo,
    pageSize: queryParams.pageSize
  }
  if (queryParams.no?.trim()) params.no = queryParams.no.trim()
  if (queryParams.orderNo?.trim()) params.orderNo = queryParams.orderNo.trim()
  if (queryParams.spuName?.trim()) params.spuName = queryParams.spuName.trim()
  if (queryParams.status != null && statusMap[queryParams.status]) {
    params.status = queryParams.status
  }
  if (queryParams.way === 10 || queryParams.way === 20 || queryParams.way === 30) {
    params.way = queryParams.way
  }
  return params
}

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<any>(null)
const detailTitle = computed(() =>
  detail.value?.no ? `售后详情 · ${detail.value.no}` : '售后详情'
)
const refundSubmitting = ref(false)
const refundButtonDisabled = computed(
  () =>
    asStatus(detail.value) === 50 ||
    !!detail.value?.payRefundId ||
    refundSubmitting.value
)
const refundButtonText = computed(() => {
  if (asStatus(detail.value) === 50) return '已退款'
  if (detail.value?.payRefundId) return '退款处理中'
  return '确认退款'
})

const agreeVisible = ref(false)
const agreeLoading = ref(false)
const agreeFormRef = ref()
const agreeApplyPrice = ref(0)
const agreeItemPayPrice = ref<number | null>(null)
const agreeUsePoint = ref(0)
const agreePointPrice = ref(0)
const agreeWay = ref<number | null>(null)
const agreeAreaTree = ref<any[]>([])
const agreeReturn = ref<{
  returnName?: string
  returnMobile?: string
  returnAreaId?: number
  returnDetailAddress?: string
}>({})
const agreeReturnReady = computed(
  () =>
    !!agreeReturn.value.returnName?.trim() &&
    !!agreeReturn.value.returnMobile?.trim() &&
    agreeReturn.value.returnAreaId != null &&
    !!agreeReturn.value.returnDetailAddress?.trim()
)
const agreeReturnAddressLine = computed(() =>
  formatReturnAddressLine(agreeAreaTree.value, agreeReturn.value)
)
const agreeForm = ref<{ id?: number; refundPriceYuan?: number }>({})
const agreeMaxYuan = computed(() => {
  const applyYuan = fenToYuanNumber(agreeApplyPrice.value)
  if (agreeItemPayPrice.value == null) return applyYuan
  return Math.min(applyYuan, fenToYuanNumber(agreeItemPayPrice.value))
})
const agreeRules = reactive({
  refundPriceYuan: [
    { required: true, message: '请输入同意退款金额', trigger: 'blur' },
    {
      validator: (_rule: any, value: number, callback: (e?: Error) => void) => {
        if (value == null || value <= 0) {
          callback(new Error('退款金额必须大于 0'))
          return
        }
        if (value > agreeMaxYuan.value) {
          callback(new Error('退款金额不能高于申请金额'))
          return
        }
        callback()
      },
      trigger: 'blur'
    }
  ]
})

const disagreeVisible = ref(false)
const disagreeLoading = ref(false)
const disagreeForm = ref<{ id?: number; auditReason: string }>({ auditReason: '' })

const exchangeVisible = ref(false)
const exchangeLoading = ref(false)
const exchangeFormRef = ref()
const exchangeExpressList = ref<DeliveryExpressSimpleVO[]>([])
const exchangeForm = ref<{ id?: number; logisticsId?: number; logisticsNo: string }>({
  logisticsNo: ''
})
const exchangeRules = reactive({
  logisticsId: [{ required: true, message: '请选择物流公司', trigger: 'change' }],
  logisticsNo: [{ required: true, message: '请输入物流单号', trigger: 'blur' }]
})

const getList = async () => {
  loading.value = true
  try {
    const data = await MerchantAfterSaleApi.getAfterSalePage(buildQuery())
    list.value = data?.list || []
    total.value = data?.total || 0
  } catch (e) {
    console.warn('售后列表加载失败', e)
    list.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}
const resetQuery = () => {
  queryParams.no = ''
  queryParams.orderNo = ''
  queryParams.spuName = ''
  queryParams.status = undefined
  queryParams.way = undefined
  handleQuery()
}

const openDetail = async (id: number) => {
  detailVisible.value = true
  detailLoading.value = true
  detail.value = null
  try {
    detail.value = await MerchantAfterSaleApi.getAfterSale(id)
  } catch {
    message.error('加载售后详情失败')
    detailVisible.value = false
  } finally {
    detailLoading.value = false
  }
}

const refreshAfterAction = async (id?: number) => {
  await getList()
  if (id && detailVisible.value) {
    try {
      detail.value = await MerchantAfterSaleApi.getAfterSale(id)
    } catch {
      /* ignore */
    }
  }
}

const openAgree = async (row: any) => {
  let source = row
  // 列表行可能没有 orderItem，尽量拉详情拿实付上限
  if (row?.id && !row?.orderItem) {
    try {
      source = (await MerchantAfterSaleApi.getAfterSale(row.id)) || row
    } catch {
      source = row
    }
  }
  agreeForm.value = {
    id: source.id,
    refundPriceYuan: fenToYuanNumber(source.refundPrice || 0)
  }
  agreeApplyPrice.value = source.refundPrice || 0
  agreeItemPayPrice.value = source.orderItem?.payPrice ?? null
  agreeUsePoint.value = source.usePoint || source.orderItem?.usePoint || 0
  agreePointPrice.value = source.pointPrice || source.orderItem?.pointPrice || 0
  agreeWay.value = source.way ?? null
  agreeReturn.value = {}
  if (agreeWay.value === 20 || agreeWay.value === 30) {
    if (!agreeAreaTree.value.length) {
      agreeAreaTree.value = (await getAreaTree()) || []
    }
    try {
      const shop = await MerchantShopApi.getShopInfo()
      agreeReturn.value = {
        returnName: shop?.returnName,
        returnMobile: shop?.returnMobile,
        returnAreaId: shop?.returnAreaId,
        returnDetailAddress: shop?.returnDetailAddress
      }
    } catch {
      agreeReturn.value = {}
    }
  }
  agreeVisible.value = true
  nextTick(() => agreeFormRef.value?.clearValidate())
}

const submitAgree = async () => {
  if ((agreeWay.value === 20 || agreeWay.value === 30) && !agreeReturnReady.value) {
    message.warning('请先在店铺设置中配置退货地址')
    return
  }
  if (agreeWay.value !== 30) {
    await agreeFormRef.value?.validate()
  }
  agreeLoading.value = true
  try {
    const payload: { id: number; refundPrice?: number } = { id: agreeForm.value.id! }
    if (agreeWay.value === 30) {
      payload.refundPrice = 0
    } else {
      payload.refundPrice = yuanToFen(agreeForm.value.refundPriceYuan!)
    }
    await MerchantAfterSaleApi.agreeAfterSale(payload)
    message.success('已同意售后')
    agreeVisible.value = false
    await refreshAfterAction(agreeForm.value.id)
  } finally {
    agreeLoading.value = false
  }
}

const openExchangeDelivery = async (row: any) => {
  exchangeForm.value = { id: row.id, logisticsId: undefined, logisticsNo: '' }
  if (!exchangeExpressList.value.length) {
    try {
      exchangeExpressList.value =
        (await MerchantExpressApi.getSimpleDeliveryExpressList()) || []
    } catch {
      exchangeExpressList.value = []
    }
  }
  exchangeVisible.value = true
  nextTick(() => exchangeFormRef.value?.clearValidate())
}

const submitExchangeDelivery = async () => {
  await exchangeFormRef.value?.validate()
  exchangeLoading.value = true
  try {
    await MerchantAfterSaleApi.deliveryExchange({
      id: exchangeForm.value.id!,
      logisticsId: exchangeForm.value.logisticsId!,
      logisticsNo: exchangeForm.value.logisticsNo.trim()
    })
    message.success('已发出换货')
    exchangeVisible.value = false
    await refreshAfterAction(exchangeForm.value.id)
  } finally {
    exchangeLoading.value = false
  }
}

const openDisagree = (row: any) => {
  disagreeForm.value = { id: row.id, auditReason: '' }
  disagreeVisible.value = true
}

const submitDisagree = async () => {
  if (!disagreeForm.value.auditReason?.trim()) {
    message.warning('请填写拒绝原因')
    return
  }
  disagreeLoading.value = true
  try {
    await MerchantAfterSaleApi.disagreeAfterSale({
      id: disagreeForm.value.id!,
      auditReason: disagreeForm.value.auditReason.trim()
    })
    message.success('已拒绝售后')
    disagreeVisible.value = false
    await refreshAfterAction(disagreeForm.value.id)
  } finally {
    disagreeLoading.value = false
  }
}

const handleReceive = async (id: number) => {
  try {
    await message.confirm('确认已收到退货商品？')
    await MerchantAfterSaleApi.receiveAfterSale(id)
    message.success('已确认收货')
    await refreshAfterAction(id)
  } catch {}
}

const handleRefuse = async (id: number) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入拒绝收货原因', '拒绝收货', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputPattern: /\S+/,
      inputErrorMessage: '请填写原因'
    })
    await MerchantAfterSaleApi.refuseAfterSale({ id, refuseMemo: value })
    message.success('已拒绝收货')
    await refreshAfterAction(id)
  } catch {}
}

const handleRefund = async (id: number) => {
  if (detail.value?.id === id && refundButtonDisabled.value) return
  try {
    await message.confirm('确认向买家退款？此操作将发起支付退款。')
    refundSubmitting.value = true
    await MerchantAfterSaleApi.refundAfterSale(id)
    message.success('已发起退款')
    await refreshAfterAction(id)
  } catch {
  } finally {
    refundSubmitting.value = false
  }
}

onMounted(() => {
  getList()
})
onActivated(() => {
  getList()
})
</script>
